import { refreshTokenRequest } from './requests/RefreshTokenService'

import { ASYNC_STORAGE_KEYS } from '~constants'
import { secureStore, decodeAccessToken, logoutMessageShown } from '~utils'

const { USER_TOKEN, USER_REFRESH_TOKEN } = ASYNC_STORAGE_KEYS

export type Token = string

// FIXME: set the time when backend will set the real expiration time (3 minutes UTC)
const TIME_BEFORE_TOKEN_EXPIRATION = 3 * 1000
let isRefreshingToken = false

export async function setToken(token: Token): Promise<void> {
  return await secureStore.setItem(USER_TOKEN, token)
}

export async function getToken(): Promise<Token | undefined> {
  const token = await secureStore.getItem(USER_TOKEN)
  if (!token) {
    return
  }
  const newToken = await refreshTokenIfNeeded(token)
  return newToken
}

export async function deleteToken() {
  const token = await secureStore.getItem(USER_TOKEN)

  if (!token) return

  return secureStore.removeItem(USER_TOKEN)
}

export async function getRefreshToken(): Promise<Token | undefined> {
  const refreshToken = await secureStore.getItem(USER_REFRESH_TOKEN)

  return refreshToken || undefined
}

export async function setRefreshToken(token: Token): Promise<void> {
  return await secureStore.setItem(USER_REFRESH_TOKEN, token)
}

export async function deleteRefreshToken() {
  const refreshToken = await secureStore.getItem(USER_REFRESH_TOKEN)
  return refreshToken && (await secureStore.removeItem(USER_REFRESH_TOKEN))
}

export async function refreshTokenIfNeeded(token: Token): Promise<Token> {
  try {
    const decodedToken = decodeAccessToken(token)
    // Convert unix time to UTC
    const expirationTime = (decodedToken?.exp || 0) * 1000
    // If token doesn't have expiration time we are returning this token
    // here what is logout  message shown
    if (!expirationTime || logoutMessageShown) {
      return token
    }

    const isExpired = expirationTime - TIME_BEFORE_TOKEN_EXPIRATION < Math.round(Date.now())

    // If token is expired and app is trying to fetch new token using refresh token
    if (isExpired && !isRefreshingToken) {
      isRefreshingToken = true

      const refreshToken = await secureStore.getItem(USER_REFRESH_TOKEN)
      if (!refreshToken) {
        isRefreshingToken = false
        return token
      }

      const { newAccessToken, newRefreshToken } = await refreshTokenRequest()

      await setToken(newAccessToken)
      await setRefreshToken(newRefreshToken)

      isRefreshingToken = false

      return newAccessToken
    }

    // If token is not expired app is returning token that was provided in params
    return token
  } catch {
    isRefreshingToken = false
    return token
  }
}
